import {
  SITE_DIARY_ACTIVITY_LABELS,
  SITE_DIARY_INCIDENT_HINT,
  SITE_DIARY_PHOTO_HINT,
  SITE_DIARY_WEATHER_LABELS,
  SiteDiaryActivity,
  intranetModuleReportSchema,
  siteDiaryDraftSchema,
} from '@gb-ticket/shared-types';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, Loader2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError, apiFetch } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';

/** Mail deep link `/intranet/modul/:instanceId/bericht/:reportId` (Entscheidung 20). */
export function SiteDiaryReportPage() {
  const { instanceId = '', reportId = '' } = useParams();
  const report = useQuery({
    queryKey: ['intranet', 'modules', instanceId, 'reports', reportId],
    enabled: instanceId.length > 0 && reportId.length > 0,
    queryFn: () =>
      apiFetch(`/intranet/modules/${instanceId}/reports/${reportId}`, intranetModuleReportSchema),
  });

  const title = report.data?.runningNumber ?? 'Bautagesbericht';
  usePageTitle(title);

  if (report.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Bericht wird geladen …
      </p>
    );
  }

  if (report.isError || !report.data) {
    return (
      <section className="space-y-4">
        <PlatformPageHeader
          title="Bericht"
          description="Bautagesbericht"
          action={
            <Button asChild variant="outline" size="sm" className="min-h-11">
              <Link to={instanceId ? `/intranet/modul/${instanceId}` : '/intranet'}>
                <ArrowLeftIcon aria-hidden />
                Zurück
              </Link>
            </Button>
          }
        />
        <p role="alert" className="text-sm text-destructive">
          {report.error instanceof ApiRequestError
            ? report.error.message
            : 'Diesen Bericht gibt es nicht.'}
        </p>
      </section>
    );
  }

  const parsed = siteDiaryDraftSchema.safeParse(report.data.payload ?? {});
  const payload = parsed.success
    ? parsed.data
    : {
        runningNumber: '',
        date: '',
        project: '',
        address: '',
        colleagues: '',
        activities: '' as (typeof SiteDiaryActivity)[keyof typeof SiteDiaryActivity] | '',
        activityOther: '',
        notes: '',
        sguNotes: '',
        incidents: '',
        extraEffort: false,
        extraEffortReason: '',
        weather: '' as string,
        temperature: '',
      };
  const pdfReady = Boolean(report.data.pdfStorageKey);

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title={title}
        description="Bautagesbericht"
        action={
          <Button asChild variant="outline" size="sm" className="min-h-11">
            <Link to={`/intranet/modul/${instanceId}`}>
              <ArrowLeftIcon aria-hidden />
              Zurück
            </Link>
          </Button>
        }
      />
      <dl className={`${intranetPanelClassName} grid max-w-3xl gap-4 text-sm`}>
        <div>
          <dt className="font-medium">Laufende Nummer</dt>
          <dd className="mt-1">
            {payload.runningNumber.trim().length > 0
              ? payload.runningNumber.trim()
              : (report.data.runningNumber ?? '—')}
          </dd>
        </div>
        <div>
          <dt className="font-medium">Datum</dt>
          <dd className="mt-1">{payload.date || '—'}</dd>
        </div>
        <div>
          <dt className="font-medium">Bauprojekt</dt>
          <dd className="mt-1">{payload.project || '—'}</dd>
        </div>
        <div>
          <dt className="font-medium">Adresse Kunde/Baustelle</dt>
          <dd className="mt-1 whitespace-pre-wrap">{payload.address || '—'}</dd>
        </div>
        <div>
          <dt className="font-medium">Beteiligte Kollegen und Arbeitszeiten</dt>
          <dd className="mt-2 whitespace-pre-wrap">
            {payload.colleagues.trim() ? payload.colleagues : '—'}
          </dd>
        </div>
        <div>
          <dt className="font-medium">Tätigkeit</dt>
          <dd className="mt-1">
            {payload.activities ? SITE_DIARY_ACTIVITY_LABELS[payload.activities] : '—'}
            {payload.activities === SiteDiaryActivity.SONSTIGES && payload.activityOther
              ? ` (${payload.activityOther})`
              : ''}
          </dd>
        </div>
        {payload.notes ? (
          <div>
            <dt className="font-medium">Notizen</dt>
            <dd className="mt-1 whitespace-pre-wrap">{payload.notes}</dd>
          </div>
        ) : null}
        {payload.sguNotes ? (
          <div>
            <dt className="font-medium">
              SGU-Besonderheiten (Sicherheit, Gesundheit, Umweltschutz)
            </dt>
            <dd className="mt-1 whitespace-pre-wrap">{payload.sguNotes}</dd>
          </div>
        ) : null}
        <div>
          <dt className="font-medium">Besondere Vorkommnisse</dt>
          <dd className="mt-1 whitespace-pre-wrap">{payload.incidents || '—'}</dd>
          <p className="mt-1 text-muted-foreground">{SITE_DIARY_INCIDENT_HINT}</p>
        </div>
        <div>
          <dt className="font-medium">Montagemehraufwand</dt>
          <dd className="mt-1">
            {payload.extraEffort ? `Ja – ${payload.extraEffortReason || '—'}` : 'Nein'}
          </dd>
        </div>
        <div>
          <dt className="font-medium">Wetter</dt>
          <dd className="mt-1">
            {payload.weather
              ? SITE_DIARY_WEATHER_LABELS[payload.weather as keyof typeof SITE_DIARY_WEATHER_LABELS]
              : '—'}
          </dd>
        </div>
        <div>
          <dt className="font-medium">Temperatur</dt>
          <dd className="mt-1">{payload.temperature.trim() ? payload.temperature : '—'}</dd>
        </div>
        <div>
          <dt className="font-medium">Fotodokumentation</dt>
          <dd className="mt-2 flex flex-wrap gap-2">
            {report.data.photos.length === 0
              ? '—'
              : report.data.photos.map((photo, index) => (
                  <img
                    key={photo.id}
                    src={photo.url}
                    alt={`Foto ${index + 1}`}
                    className="h-20 w-28 rounded object-cover"
                  />
                ))}
          </dd>
          <p className="mt-2 text-muted-foreground">{SITE_DIARY_PHOTO_HINT}</p>
        </div>
      </dl>
      {pdfReady ? (
        <Button
          type="button"
          className="min-h-11"
          onClick={() => void downloadPdf(instanceId, report.data.id)}
        >
          PDF herunterladen
        </Button>
      ) : (
        <Button type="button" className="min-h-11" disabled>
          PDF folgt
        </Button>
      )}
    </section>
  );
}

async function downloadPdf(instanceId: string, reportId: string) {
  try {
    const response = await fetch(`/api/intranet/modules/${instanceId}/reports/${reportId}/pdf`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('PDF konnte nicht geladen werden.');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'Bautagesbericht.pdf';
    anchor.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'PDF herunterladen fehlgeschlagen.');
  }
}
